// tslint:disable:cyclomatic-complexity
import dayjs from 'dayjs';
import { Month, RepeatParsingResult, Weekday } from './soonerorlater';

export const getTodayString = (): string => dayjs().format('YYYY-MM-DD');

export function getCalendarDayString(from?: string | Date | dayjs.Dayjs): string {
    return dayjs(from).format('YYYY-MM-DD');
}

export function getYesterdayCalendarDay(from?: string | Date | dayjs.Dayjs): string {
    return dayjs(from).subtract(1, 'day').format('YYYY-MM-DD');
}

const mapWeekDayToNum = (weekDay: Weekday) => {
    switch (weekDay) {
        case 'monday':
            return 1;
        case 'tuesday':
            return 2;
        case 'wednesday':
            return 3;
        case 'thursday':
            return 4;
        case 'friday':
            return 5;
        case 'saturday':
            return 6;
        case 'sunday':
            return 7;
    }
};

const mapMonthToNum = (month: Month) => {
    switch (month) {
        case 'january':
            return 0;
        case 'february':
            return 1;
        case 'march':
            return 2;
        case 'april':
            return 3;
        case 'may':
            return 4;
        case 'june':
            return 5;
        case 'july':
            return 6;
        case 'august':
            return 7;
        case 'september':
            return 8;
        case 'october':
            return 9;
        case 'november':
            return 10;
        case 'december':
            return 11;
    }
};

const addTime = (date: dayjs.Dayjs, startTime?: RepeatParsingResult['startTime']) => {
    if (!startTime) return date.set('hour', 0).set('minute', 0);
    return date.set('hour', startTime.hours).set('minute', startTime.minutes ?? 0);
};

export const firstOccurrence = ({ repeats, startTime, weekdays, monthDay, month }: RepeatParsingResult) => {
    const today = dayjs();

    if (repeats) {
        if (repeats === 'daily') return addTime(today, startTime);
        if (repeats === 'weekly') {
            if (weekdays) {
                const weekDaysList = [...weekdays];
                if (weekDaysList.length > 0) {
                    const weekDayNums = weekDaysList.map(mapWeekDayToNum);
                    weekDayNums.sort();
                    const todaysWeekDay = today.get('day');
                    const largerWeekDays = weekDayNums.filter(wd => wd > todaysWeekDay);
                    const nextWeekDay = largerWeekDays.length > 0 ? largerWeekDays[0] : weekDayNums[0];
                    return addTime(
                        today.add(nextWeekDay > todaysWeekDay ? nextWeekDay - todaysWeekDay : 7 - todaysWeekDay + nextWeekDay, 'day'),
                        startTime,
                    );
                }
            }
            return addTime(today, startTime);
        }
        if (repeats === 'monthly') {
            if (!monthDay) return addTime(today, startTime);

            const todaysDayOfMonth = today.get('date');
            if (todaysDayOfMonth < monthDay) return addTime(today.set('date', monthDay), startTime);
            return addTime(today.set('date', monthDay).add(1, 'month'), startTime);
        }
        if (repeats === 'yearly') {
            if (!monthDay || !month) return addTime(today, startTime);

            const todaysDayOfMonth = today.get('date');
            const todaysMonth = today.get('month');
            const monthNum = mapMonthToNum(month);

            if (todaysMonth < monthNum || (todaysMonth === monthNum && todaysDayOfMonth < monthDay))
                return addTime(today.set('date', monthDay).set('month', monthNum), startTime);
            return addTime(today.set('date', monthDay).set('month', monthNum).add(1, 'year'), startTime);
        }
    }

    return addTime(today, startTime);
};

export const nextOccurrence = (
    { repeats, startTime, interval, weekdays, monthDay, month }: RepeatParsingResult,
    startDate: dayjs.Dayjs,
) => {
    const today = startDate;

    if (repeats) {
        if (repeats === 'daily') return addTime(today.add(interval ? interval : 1, 'day'), startTime);
        if (repeats === 'weekly') {
            if (weekdays) {
                const weekDaysList = [...weekdays];
                if (weekDaysList.length > 0) {
                    const weekDayNums = weekDaysList.map(mapWeekDayToNum);
                    weekDayNums.sort();
                    const todaysWeekDay = today.get('day');
                    const largerWeekDays = weekDayNums.filter(wd => wd > todaysWeekDay);
                    const nextWeekDay = largerWeekDays.length > 0 ? largerWeekDays[0] : weekDayNums[0];
                    return addTime(
                        today
                            .add(nextWeekDay > todaysWeekDay ? nextWeekDay - todaysWeekDay : 7 - todaysWeekDay + nextWeekDay, 'day')
                            .add(interval ? interval - 1 : 0, 'week'),
                        startTime,
                    );
                }
            }
            return addTime(today.add(interval ? interval : 1, 'week'), startTime);
        }
        if (repeats === 'monthly') {
            if (!monthDay) return addTime(today.add(interval ? interval : 1, 'month'), startTime);

            const todaysDayOfMonth = today.get('date');
            if (todaysDayOfMonth < monthDay)
                return addTime(today.set('date', monthDay).add(interval ? interval - 1 : 0, 'month'), startTime);
            return addTime(today.set('date', monthDay).add(interval ? interval : 1, 'month'), startTime);
        }
        if (repeats === 'yearly') {
            if (!monthDay || !month) return addTime(today.add(interval ? interval : 1, 'year'), startTime);

            const todaysDayOfMonth = today.get('date');
            const todaysMonth = today.get('month');
            const monthNum = mapMonthToNum(month);

            if (todaysMonth < monthNum || (todaysMonth === monthNum && todaysDayOfMonth < monthDay))
                return addTime(
                    today
                        .set('date', monthDay)
                        .set('month', monthNum)
                        .add(interval ? interval - 1 : 0, 'year'),
                    startTime,
                );
            return addTime(
                today
                    .set('date', monthDay)
                    .set('month', monthNum)
                    .add(interval ? interval : 1, 'year'),
                startTime,
            );
        }
    }
    return null;
};

export const isInPast = (d: dayjs.Dayjs) => d.isBefore(dayjs().hour(0).minute(0).second(0).millisecond(0));
export const isInPastFromNow = (d: dayjs.Dayjs) => d.isBefore(dayjs());
export type TimeOfDay = 'MorningRitual' | 'Overdue' | 'All day' | 'Morning' | 'Afternoon' | 'Evening' | 'EveningRitual';

const isoCalendarDayPattern = /^\d{4}-\d{2}-\d{2}$/;

export function isCalendarDayString(value: unknown): value is string {
    return typeof value === 'string' && isoCalendarDayPattern.test(value);
}

export function verifyDateStringOrThrow(value: string | null | undefined): asserts value is string {
    if (value == null || typeof value !== 'string' || value.trim() === '' || !isoCalendarDayPattern.test(value)) {
        throw new Error('Invalid date');
    }
}

function ordinalSuffix(day: number): string {
    const mod100 = day % 100;
    if (mod100 >= 11 && mod100 <= 13) return 'th';
    const mod10 = day % 10;
    if (mod10 === 1) return 'st';
    if (mod10 === 2) return 'nd';
    if (mod10 === 3) return 'rd';
    return 'th';
}

export function formatCalendarDayLabel(
    calendarDay: string,
    opts?: { todayKey?: string; todayLabel?: string; yesterdayLabel?: string; locale?: string },
): string {
    if (!isCalendarDayString(calendarDay)) return calendarDay;
    const todayKey = opts?.todayKey ?? getTodayString();
    if (calendarDay === todayKey) return opts?.todayLabel ?? 'Today';
    if (calendarDay === getYesterdayCalendarDay(todayKey)) return opts?.yesterdayLabel ?? 'Yesterday';
    const locale = opts?.locale ?? 'en';
    const d = dayjs(calendarDay).locale(locale);
    if (!d.isValid()) return calendarDay;
    if (locale === 'en') {
        const day = d.date();
        return `${d.format('MMMM')} ${day}${ordinalSuffix(day)}`;
    }
    return d.format('D MMMM');
}

export function formatCalendarDayShort(calendarDay: string, opts?: { locale?: string }): string {
    if (!isCalendarDayString(calendarDay)) return calendarDay;
    const d = dayjs(calendarDay).locale(opts?.locale ?? 'en');
    if (!d.isValid()) return calendarDay;
    return d.format('D MMM YYYY');
}

export function getTimeOfDay(dueDate: string | null | undefined): TimeOfDay {
    if (dueDate === null || dueDate === undefined) {
        return 'All day';
    }

    const date = dayjs(dueDate);

    if (isInPast(date)) return 'Overdue';

    const hour = date.hour();
    if (hour === 0) return 'All day';
    if (hour < 12) return 'Morning';
    if (hour < 17) return 'Afternoon';
    return 'Evening';
}
